import { createSlice, current } from '@reduxjs/toolkit';
import {
	getGeodeticDatumList,
	getGeodeticDatumListQuery,
	getGeodeticDatum,
	addNewGeodeticDatum,
	updateGeodeticDatum,
	deleteGeodeticDatum,
	checkGeodeticDatumExists,
} from './thunk';

export const initialState = {
	itemGeodeticDatumList: [],
	itemGeodeticDatum: null,
	isGeodeticDatumLoading: false,
	isGeodeticDatumSuccess: false,
	dataGeodeticDatum: null,
	statementList: [],
	statementListSelect: [],
	isLoadingList: false,
	isAdding: false,
	isAdded: false,
	isAddError: false,
	isUpdating: false,
	isUpdated: false,
	isUpdateError: false,
	isDeleting: false,
	isDeleted: false,
	isDeleteError: false,
	requireCloseModal: false,
	registerDisabled: false,
	requireReloadSelectionItemsGrid: false,
	exists: false,
};

const GeodeticDatumSlice = createSlice({
	name: 'GeodeticDatum',
	initialState,
	reducers: {
		setRequireCloseModal(state, action) {
			state.requireCloseModal = action.payload;
		},
	},
	extraReducers: builder => {
		builder.addCase(getGeodeticDatumList.pending, state => {
			state.isLoadingList = true;
			state.isGeodeticDatumSuccess = false;
		});

		builder.addCase(getGeodeticDatumList.fulfilled, (state, action) => {
			let statementList = action.payload;
			if (action.payload?.data) {
				statementList = action.payload.data;
			}
			statementList = Array.isArray(statementList) ? statementList : [];
			const statementListSelect = statementList.map(item => ({
				item,
				value: item._id,
				label: item.name,
			}));

			state.itemGeodeticDatumList = statementList;
			state.statementList = statementList;
			state.statementListSelect = statementListSelect;
			state.isLoadingList = false;
			state.isGeodeticDatumSuccess = true;
		});

		builder.addCase(getGeodeticDatumList.rejected, (state, action) => {
			state.error = action.payload || null;
			state.isLoadingList = false;
			state.isGeodeticDatumSuccess = false;
			state.itemGeodeticDatumList = [];
		});

		builder.addCase(getGeodeticDatumListQuery.pending, state => {
			state.isLoadingList = true;
			state.isGeodeticDatumSuccess = false;
		});

		builder.addCase(getGeodeticDatumListQuery.fulfilled, (state, action) => {
			let statementList = action.payload;
			if (action.payload?.data) {
				statementList = action.payload.data;
			}
			state.itemGeodeticDatumList = Array.isArray(statementList) ? statementList : [];
			state.statementList = state.itemGeodeticDatumList;
			state.isLoadingList = false;
			state.isGeodeticDatumSuccess = true;
		});

		builder.addCase(getGeodeticDatumListQuery.rejected, (state, action) => {
			state.isLoadingList = false;
			state.isGeodeticDatumSuccess = false;
			state.itemGeodeticDatumList = [];
		});

		builder.addCase(getGeodeticDatum.pending, state => {
			state.isGeodeticDatumLoading = true;
			state.isGeodeticDatumSuccess = false;
		});

		builder.addCase(getGeodeticDatum.fulfilled, (state, action) => {
			state.itemGeodeticDatum = action.payload;
			state.isGeodeticDatumLoading = false;
			state.isGeodeticDatumSuccess = true;
		});

		builder.addCase(getGeodeticDatum.rejected, (state, action) => {
			state.error = action.payload || null;
			state.isGeodeticDatumLoading = false;
			state.isGeodeticDatumSuccess = false;
		});

		builder.addCase(addNewGeodeticDatum.pending, state => {
			state.isLoadingList = true;
			state.isAdding = true;
			state.isAdded = false;
			state.isAddError = false;
		});

		builder.addCase(addNewGeodeticDatum.fulfilled, (state, action) => {
			state.itemGeodeticDatumList.push(action.payload);
			state.statementList.push(action.payload);
			state.statementListSelect.push({
				item: action.payload,
				value: action.payload._id,
				label: action.payload.name,
			});
			state.isLoadingList = false;
			state.isAdding = false;
			state.isAdded = true;
			state.requireCloseModal = true;
		});

		builder.addCase(addNewGeodeticDatum.rejected, (state, action) => {
			state.error = action.payload || null;
			state.isLoadingList = false;
			state.isAdding = false;
			state.isAddError = true;
		});

		builder.addCase(updateGeodeticDatum.pending, state => {
			state.isLoadingList = true;
			state.isUpdating = true;
			state.isUpdated = false;
			state.isUpdateError = false;
		});

		builder.addCase(updateGeodeticDatum.fulfilled, (state, action) => {
			state.itemGeodeticDatumList = state.itemGeodeticDatumList.map(item =>
				item._id === action.payload._id ? action.payload : item
			);
			state.statementList = state.statementList.map(item =>
				item._id === action.payload._id ? action.payload : item
			);
			state.statementListSelect = state.statementListSelect.map(item =>
				item.value === action.payload._id
					? {
							item: action.payload,
							value: action.payload._id,
							label: action.payload.name,
					  }
					: item
			);
			state.isLoadingList = false;
			state.isUpdating = false;
			state.isUpdated = true;
			state.requireCloseModal = true;
		});

		builder.addCase(updateGeodeticDatum.rejected, (state, action) => {
			state.error = action.payload || null;
			state.isLoadingList = false;
			state.isUpdating = false;
			state.isUpdateError = true;
		});

		builder.addCase(deleteGeodeticDatum.pending, state => {
			state.isLoadingList = true;
			state.isDeleting = true;
			state.isDeleted = false;
			state.isDeleteError = false;
		});

		builder.addCase(deleteGeodeticDatum.fulfilled, (state, action) => {
			state.itemGeodeticDatumList = state.itemGeodeticDatumList.filter(
				item => item._id !== action.payload._id
			);
			state.statementList = state.statementList.filter(
				item => item._id !== action.payload._id
			);
			state.statementListSelect = state.statementListSelect.filter(
				item => item.value !== action.payload._id
			);
			state.isLoadingList = false;
			state.isDeleting = false;
			state.isDeleted = true;
			state.requireCloseModal = true;
		});

		builder.addCase(deleteGeodeticDatum.rejected, (state, action) => {
			state.error = action.payload || null;
			state.isLoadingList = false;
			state.isDeleting = false;
			state.isDeleteError = true;
		});

		builder.addCase(checkGeodeticDatumExists.pending, state => {
			state.isLoadingList = true;
		});

		builder.addCase(checkGeodeticDatumExists.fulfilled, (state, action) => {
			state.exists = action.payload.exists;
			state.isLoadingList = false;
		});

		builder.addCase(checkGeodeticDatumExists.rejected, (state, action) => {
			state.isLoadingList = false;
		});
	},
});

export const { setRequireCloseModal } = GeodeticDatumSlice.actions;

export default GeodeticDatumSlice.reducer;
